import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Card, Tabs } from '@/components/ui';
import { useMe } from '@/auth/useMe';
import { listFollowUps, listMeetings } from '@/data/client';
import type { Deal } from '@/data/types';
import { longDate, todayIso } from '@/components/crm/FollowUp';
import { DraftModal } from '@/components/crm/EmailAssistant';
import { GoogleConnectionCard } from '@/components/crm/GoogleConnect';
import { MeetingList } from '@/components/crm/MeetingsPanel';

/* The AI-written to-do list lands here in a later phase. For now: follow-ups
   due, your upcoming meetings and your Google connection. */
export function Today() {
  const { me } = useMe();
  const { data: meetings = [] } = useQuery({ queryKey: ['meetings', { mine: true }], queryFn: () => listMeetings({ mine: true }) });
  const upcoming = meetings.filter((m) => Date.parse(m.endAt) > Date.now());
  const first = me?.name?.split(' ')[0];
  return (
    <div className="crm-record">
      <div className="crm-record__title">
        <h2>{first ? `Good to see you, ${first}` : 'Welcome'}</h2>
        <div className="crm-record__sub"><span>{me?.workspace.name}</span></div>
      </div>
      <FollowUps />
      <div className="crm-grid2 crm-grid2--top">
        <Card padding={false} className="crm-section">
          <div className="crm-section__head"><h3>Your upcoming meetings</h3></div>
          {upcoming.length
            ? <MeetingList meetings={upcoming} tz={me?.workspace.timezone ?? 'America/Chicago'} showAccount />
            : <p className="crm-cell-muted">Nothing scheduled. Schedule from any company, contact or deal.</p>}
        </Card>
        <Card padding={false} className="crm-section">
          <div className="crm-section__head"><h3>Google Calendar</h3></div>
          <GoogleConnectionCard />
        </Card>
      </div>
    </div>
  );
}

type Window = 'week' | 'overdue' | 'later';
const addDays = (iso: string, n: number) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return d.toLocaleDateString('en-CA'); };

/* Deals someone said to come back to — open ones and lost ones (the revisit
   list). "This week" is anything due in the next 7 days or overdue by up to 30;
   older misses wait under Overdue so a backlog never buries this week. */
function FollowUps() {
  const [scope, setScope] = useState<'mine' | 'all'>('mine');
  const [win, setWin] = useState<Window>('week');
  const [nurturing, setNurturing] = useState<Deal | null>(null);
  const { data: deals = [] } = useQuery({ queryKey: ['followUps', scope], queryFn: () => listFollowUps(scope) });
  const today = todayIso();
  const inWindow = (d: Deal) => {
    const at = d.followUpAt!;
    if (win === 'week') return at >= addDays(today, -30) && at <= addDays(today, 7);
    if (win === 'overdue') return at < today;
    return at > addDays(today, 7);
  };
  const rows = deals.filter(inWindow);
  const count = (w: Window) => deals.filter((d) => { const at = d.followUpAt!; return w === 'week' ? at >= addDays(today, -30) && at <= addDays(today, 7) : w === 'overdue' ? at < today : at > addDays(today, 7); }).length;
  return (
    <Card padding={false} className="crm-section">
      <div className="crm-section__head">
        <h3>Follow-ups</h3>
        <Tabs items={[{ value: 'mine', label: 'Mine' }, { value: 'all', label: 'Everyone' }]} value={scope} onChange={(v) => setScope(v as 'mine' | 'all')} />
      </div>
      <Tabs items={[
        { value: 'week', label: `This week (${count('week')})` }, { value: 'overdue', label: `Overdue (${count('overdue')})` }, { value: 'later', label: `Later (${count('later')})` },
      ]} value={win} onChange={(v) => setWin(v as Window)} />
      {rows.length ? (
        <ul className="crm-followups" style={{ marginTop: 12 }}>
          {rows.slice(0, 50).map((d) => (
            <li key={d.id}>
              <div>
                <Link to={`/deals/${d.id}`}>{d.companyName}</Link>
                <span className="crm-cell-sub">
                  {d.status === 'lost' ? `Lost${d.lostReason ? ` · ${d.lostReason}` : ''}` : d.stageName}
                  {scope === 'all' && d.ownerName ? ` · ${d.ownerName}` : ''}{d.followUpNote ? ` — ${d.followUpNote}` : ''}
                </span>
              </div>
              <span className={`crm-followups__when ${d.followUpAt! <= today ? 'is-due' : ''}`}>
                {longDate(d.followUpAt!)}
                {d.stageNurture && d.status === 'open' && (
                  <button type="button" className="crm-linkbtn crm-followups__action" onClick={() => setNurturing(d)}>Draft nurture email</button>
                )}
              </span>
            </li>
          ))}
          {rows.length > 50 && <li className="crm-cell-muted">…and {rows.length - 50} more</li>}
        </ul>
      ) : <p className="crm-cell-muted" style={{ marginTop: 12 }}>Nothing here. Set a follow-up on any deal, lost ones included.</p>}
      {nurturing && <DraftModal dealId={nurturing.id} dealName={nurturing.companyName} kind="nurture" onClose={() => setNurturing(null)} />}
    </Card>
  );
}
